/**
 * Domain Pack Library Page
 * Lists all available domain packs grouped by industry with search, filter, and generation capabilities
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Plus,
  Filter,
  Database,
  TrendingUp,
  AlertTriangle,
  Award,
  Brain,
  FileText,
  Settings,
  ChevronDown,
  ChevronRight,
  Building2,
} from 'lucide-react';

import { listDomainPacks } from '../lib/api/domainPacksApi';
import { SESSION_CACHE } from '../lib/queryCache';
import type {
  DomainPackSummary,
  DomainPackStatus,
  DomainPackFilters,
} from '../types/domainPack';
import { STATUS_NAMES, STATUS_COLORS } from '../types/domainPack';

const DomainPackLibrary: React.FC = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DomainPackFilters>({
    search: '',
    status: undefined,
    sortBy: 'updated',
    sortOrder: 'desc',
  });
  const [collapsedIndustries, setCollapsedIndustries] = useState<Set<string>>(new Set());
  const [industryFilter, setIndustryFilter] = useState<string>('');

  // Fetch domain packs
  const {
    data: domainPacks,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['domain-packs', filters.status],
    queryFn: () => listDomainPacks(filters.status),
    ...SESSION_CACHE,
  });

  // Filter, sort, and group by industry
  const { groupedPacks, industries, totalCount } = React.useMemo(() => {
    if (!domainPacks) return { groupedPacks: {} as Record<string, DomainPackSummary[]>, industries: [] as string[], totalCount: 0 };

    let filtered = [...domainPacks];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (pack) =>
          pack.domain_name.toLowerCase().includes(searchLower) ||
          pack.description.toLowerCase().includes(searchLower) ||
          (pack.industry_context || '').toLowerCase().includes(searchLower)
      );
    }

    // Industry filter
    if (industryFilter) {
      filtered = filtered.filter(
        (pack) => (pack.industry_context || 'Uncategorized') === industryFilter
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any, bVal: any;

      switch (filters.sortBy) {
        case 'name':
          aVal = a.domain_name;
          bVal = b.domain_name;
          break;
        case 'quality':
          aVal = a.quality_score;
          bVal = b.quality_score;
          break;
        case 'created':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'updated':
        default:
          aVal = new Date(a.updated_at).getTime();
          bVal = new Date(b.updated_at).getTime();
          break;
      }

      if (filters.sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    // Group by industry
    const groups: Record<string, DomainPackSummary[]> = {};
    for (const pack of filtered) {
      const industry = pack.industry_context || 'Uncategorized';
      if (!groups[industry]) {
        groups[industry] = [];
      }
      groups[industry].push(pack);
    }

    // Sort industry names alphabetically, but put "Uncategorized" last
    const sortedIndustries = Object.keys(groups).sort((a, b) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });

    return { groupedPacks: groups, industries: sortedIndustries, totalCount: filtered.length };
  }, [domainPacks, filters, industryFilter]);

  // Get all unique industries for the filter dropdown
  const allIndustries = React.useMemo(() => {
    if (!domainPacks) return [];
    const set = new Set<string>();
    for (const pack of domainPacks) {
      set.add(pack.industry_context || 'Uncategorized');
    }
    return Array.from(set).sort((a, b) => {
      if (a === 'Uncategorized') return 1;
      if (b === 'Uncategorized') return -1;
      return a.localeCompare(b);
    });
  }, [domainPacks]);

  const handleGenerateNew = () => {
    navigate('/domain-packs/generate');
  };

  const handlePackClick = (packId: string) => {
    navigate(`/domain-packs/${packId}`);
  };

  const toggleIndustry = (industry: string) => {
    setCollapsedIndustries((prev) => {
      const next = new Set(prev);
      if (next.has(industry)) {
        next.delete(industry);
      } else {
        next.add(industry);
      }
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header — constrained to the same max-width as the body so the top and
          bottom sections align at the same width. */}
      <div className="max-w-screen-2xl mx-auto px-6 pt-6">
        <div className="bg-white border border-neutral-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                Domain Research Library
              </h1>
              <p className="text-neutral-600 mt-1">
                MBB-style pre-research on business domains with AI-powered insights
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/domain-packs/admin')}
                className="flex items-center gap-2 px-4 py-2 border border-neutral-300 text-neutral-700 rounded-lg hover:bg-neutral-50 transition-colors"
              >
                <Settings className="w-5 h-5" />
                Admin Dashboard
              </button>
              <button
                onClick={handleGenerateNew}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Generate New Pack
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="mt-6 flex gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="text"
                placeholder="Search domain packs..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className="w-full pl-10 pr-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Industry Filter */}
            <select
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
              className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Industries</option>
              {allIndustries.map((industry) => (
                <option key={industry} value={industry}>
                  {industry}
                </option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={filters.status || ''}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value as DomainPackStatus | undefined,
                })
              }
              className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Status</option>
              <option value="published">Published</option>
              <option value="quality_review">Quality Review</option>
              <option value="researching">Researching</option>
              <option value="pending">Pending</option>
              <option value="archived">Archived</option>
            </select>

            {/* Sort */}
            <select
              value={`${filters.sortBy}-${filters.sortOrder}`}
              onChange={(e) => {
                const [sortBy, sortOrder] = e.target.value.split('-');
                setFilters({
                  ...filters,
                  sortBy: sortBy as any,
                  sortOrder: sortOrder as 'asc' | 'desc',
                });
              }}
              className="px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            >
              <option value="updated-desc">Recently Updated</option>
              <option value="created-desc">Recently Created</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="quality-desc">Quality (High-Low)</option>
              <option value="quality-asc">Quality (Low-High)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">
              Failed to load domain packs. Please try again.
            </p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && totalCount === 0 && (
          <div className="text-center py-12">
            <Database className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-700 mb-2">
              No domain packs found
            </h3>
            <p className="text-neutral-600 mb-6">
              {filters.search || filters.status || industryFilter
                ? 'Try adjusting your filters'
                : 'Get started by generating your first domain pack'}
            </p>
            {!filters.search && !filters.status && !industryFilter && (
              <button
                onClick={handleGenerateNew}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Generate Your First Pack
              </button>
            )}
          </div>
        )}

        {/* Grouped Domain Packs by Industry */}
        {!isLoading && totalCount > 0 && (
          <>
            <div className="mb-4 text-sm text-neutral-600">
              Showing {totalCount} domain pack
              {totalCount !== 1 ? 's' : ''} across {industries.length} industr
              {industries.length !== 1 ? 'ies' : 'y'}
            </div>

            <div className="space-y-6">
              {industries.map((industry) => {
                const packs = groupedPacks[industry];
                const isCollapsed = collapsedIndustries.has(industry);

                return (
                  <div key={industry} className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
                    {/* Industry Header */}
                    <button
                      onClick={() => toggleIndustry(industry)}
                      className="w-full flex items-center justify-between px-6 py-4 hover:bg-neutral-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {isCollapsed ? (
                          <ChevronRight className="w-5 h-5 text-neutral-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-neutral-400" />
                        )}
                        <Building2 className="w-5 h-5 text-primary-600" />
                        <h2 className="text-lg font-semibold text-neutral-900">
                          {industry}
                        </h2>
                        <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full">
                          {packs.length} pack{packs.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </button>

                    {/* Domain Pack Cards */}
                    {!isCollapsed && (
                      <div className="px-6 pb-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {packs.map((pack) => (
                            <DomainPackCard
                              key={pack.domain_pack_id}
                              pack={pack}
                              onClick={() => handlePackClick(pack.domain_pack_id)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Domain Pack Card Component
interface DomainPackCardProps {
  pack: DomainPackSummary;
  onClick: () => void;
}

const DomainPackCard: React.FC<DomainPackCardProps> = ({ pack, onClick }) => {
  const qualityColor =
    pack.quality_score >= 0.85
      ? 'text-green-600'
      : pack.quality_score >= 0.7
      ? 'text-yellow-600'
      : 'text-red-600';

  const qualityLabel =
    pack.quality_score >= 0.85
      ? 'Excellent'
      : pack.quality_score >= 0.7
      ? 'Good'
      : 'Needs Review';

  return (
    <div
      onClick={onClick}
      className="bg-white border border-neutral-200 rounded-lg p-6 hover:shadow-lg hover:border-primary-300 transition-all cursor-pointer"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-neutral-900 mb-1">
            {pack.domain_name}
          </h3>
          <span
            className={`inline-block px-2 py-1 text-xs font-medium rounded ${
              STATUS_COLORS[pack.status]
            }`}
          >
            {STATUS_NAMES[pack.status]}
          </span>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-bold ${qualityColor}`}>
            {(pack.quality_score * 100).toFixed(0)}%
          </div>
          <div className="text-xs text-neutral-500">{qualityLabel}</div>
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-neutral-600 mb-4 line-clamp-2">
        {pack.description}
      </p>

      {/* Stats */}
      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-neutral-600">
          <Database className="w-4 h-4" />
          <span>{pack.subdomain_count} Subdomains</span>
        </div>
      </div>

      {/* Footer */}
      <div className="text-xs text-neutral-500 pt-4 border-t border-neutral-100">
        Updated {new Date(pack.updated_at).toLocaleDateString()}
      </div>
    </div>
  );
};

export default DomainPackLibrary;
